// The terrain body of a peek: the elevation of a ridge (round 086 card 06 b,
// ui-map-interface.md §17.3.18 and §8 "The peek").
//
// The old chart shows a coast as a sailor sees it from the sea. The ridge
// peek carries that drawing as its body, so a hover on a ridge opens one
// peek and no second card. The words of the peek stay in the chrome face;
// the drawing follows the chart reading: the modern reading draws steps,
// the historical reading a hatched hill.
//
// **THE STEPS ARE THE RINGS.** The height of a cell is its distance from
// the water inside its own ridge, in cells, capped at the number of
// contour rings (`TERRAIN_RINGS`): a rim cell is one step high, the cell
// behind it two, and so on. The profile reads the ridge along its long
// axis and keeps the highest cell of each column, so the drawing is the
// silhouette of this ridge and not a stock staircase.
//
// The drawing is an inline SVG, written as markup on a hover: no canvas
// and no frame loop (§10.3). The depths of a ridge are read once per
// ridge and kept.

import { f1 } from '../../util/num';
import { CHART, TERRAIN_RINGS } from '../../gen/presentation';
import { card as SK, chart as SC, main as SM } from '../../gen/strings';
import type { NamedSeamount } from '../../gen/proto/aquaphobia';
import { toRoman } from '../../render/roman';
import { COL_PITCH, ROW_PITCH, cellCenter, neighborAt } from '../../world';
import { esc } from '../esc';
import type { CardSpec } from './card';
import { markSvg } from './marks';
import { bandAt, currentAt } from '../../deep';
import { soundingMetres } from '../../fast/chart/pieces/soundings';
import { groupThousands } from '../../locale/group';
import type { GameState } from '../../state';
import { bandWord } from '../chart/deep-legend';

/** The figures of the drawing. */
const T = CHART.pieces.coastal_profile;

/** What the profile reads of a ridge (`render/terrain-features.ts`). */
export interface RidgeBody {
  cells: readonly number[];
  rimCells: readonly number[];
}

/** A ridge under the pointer, as the chart furniture names it. */
export interface RidgeAt {
  body: RidgeBody;
  /** The chart's name of the ridge, or '' while the world has none. A
   *  named seamount gives its own name (R2633). */
  name: string;
  /** The seamount name the ridge carries, or undefined (R2633). */
  seamount?: NamedSeamount;
  /** The own find of the ridge that has no name yet, or undefined: the
   *  peek then carries NAME (R2633). */
  find?: NamedSeamount;
}

/** The profile of a ridge: one height per column, and the column under
 *  the pointer (−1 when the pointer is not on the ridge). */
export interface RidgeProfile {
  heights: number[];
  at: number;
}

/** The depth of each cell of a ridge, kept per ridge. */
const depthCache = new WeakMap<RidgeBody, Map<number, number>>();

/**
 * The distance of each cell from the water inside its ridge: 1 on the
 * rim, one more for each cell inward. A walk over the ridge's own cells.
 */
export function ridgeDepths(body: RidgeBody): Map<number, number> {
  const had = depthCache.get(body);
  if (had) return had;
  const inBody = new Set(body.cells);
  const depth = new Map<number, number>();
  let front: number[] = [];
  for (const c of body.rimCells) { depth.set(c, 1); front.push(c); }
  // A body with no rim cell (none on the map) is one step high.
  if (front.length === 0) for (const c of body.cells) { depth.set(c, 1); front.push(c); }
  while (front.length > 0) {
    const next: number[] = [];
    for (const c of front) {
      const d = depth.get(c)!;
      for (let k = 0; k < 6; k++) {
        const n = neighborAt(c, k);
        if (n < 0 || !inBody.has(n) || depth.has(n)) continue;
        depth.set(n, d + 1);
        next.push(n);
      }
    }
    front = next;
  }
  depthCache.set(body, depth);
  return depth;
}

/**
 * The elevation along the ridge's long axis: the cells fall into columns
 * along the wider side of the ridge's box, and each column takes the
 * height of its highest cell, capped at the ring count.
 */
export function ridgeProfile(body: RidgeBody, pointerCell: number): RidgeProfile {
  const depth = ridgeDepths(body);
  let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
  for (const c of body.cells) {
    const [x, y] = cellCenter(c);
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (y < y0) y0 = y;
    if (y > y1) y1 = y;
  }
  const alongX = x1 - x0 >= y1 - y0;
  const lo = alongX ? x0 : y0;
  const span = alongX ? x1 - x0 : y1 - y0;
  const pitch = alongX ? COL_PITCH : ROW_PITCH;
  const n = Math.max(1, Math.min(T.fig.max_columns, Math.round(span / pitch) + 1));
  const column = (c: number): number => {
    if (span <= 0) return 0;
    const [x, y] = cellCenter(c);
    return Math.min(n - 1, Math.floor((((alongX ? x : y) - lo) / span) * n));
  };
  const rings = TERRAIN_RINGS.length;
  const heights = new Array<number>(n).fill(0);
  for (const c of body.cells) {
    const k = column(c);
    heights[k] = Math.max(heights[k], Math.min(rings, depth.get(c) ?? 1));
  }
  return { heights, at: body.cells.includes(pointerCell) ? column(pointerCell) : -1 };
}

/**
 * The drawing of a profile as an SVG, in the reading of the chart. The
 * ink is the peek's own text ink (`currentColor`); the stylesheet sets the
 * face of the ring figures.
 */
export function profileSvg(p: RidgeProfile, historical: boolean): string {
  const { w, h, base_lift: lift, top_room: top, fig_room: room } = T.fig;
  const rings = TERRAIN_RINGS.length;
  const n = p.heights.length;
  const cw = (w - room) / n;
  const base = h - lift;
  const u = (base - top) / rings;
  const yOf = (v: number): number => base - v * u;
  const look = historical ? T.historical : T.modern;
  let shape: string;
  if (historical) {
    // The hill: a line through the middle of each column, from the ground
    // to the ground, filled with the engraved hatch.
    let d = `M0 ${f1(base)}`;
    p.heights.forEach((v, i) => { d += ` L${f1((i + 0.5) * cw)} ${f1(yOf(v))}`; });
    d += ` L${f1(n * cw)} ${f1(base)}Z`;
    const hp = T.historical.fig.hatch_px;
    shape = '<defs><pattern id="ck-hatch" patternUnits="userSpaceOnUse"'
      + ` width="${hp}" height="${hp}"><path d="M0 ${hp}L${hp} 0" stroke="currentColor"`
      + ` stroke-opacity="${T.historical.fig.hatch_alpha}" stroke-width="0.8"/></pattern></defs>`
      + `<path d="${d}" fill="url(#ck-hatch)" stroke="currentColor" stroke-opacity="${look.alpha}"`
      + ` stroke-width="${look.width}" stroke-linejoin="round"/>`;
  } else {
    // The steps: one tread per column at its height.
    let d = `M0 ${f1(base)}`;
    p.heights.forEach((v, i) => { d += ` L${f1(i * cw)} ${f1(yOf(v))} L${f1((i + 1) * cw)} ${f1(yOf(v))}`; });
    d += ` L${f1(n * cw)} ${f1(base)}Z`;
    shape = `<path d="${d}" fill="currentColor" fill-opacity="${T.modern.fig.fill_alpha}"`
      + ` stroke="currentColor" stroke-opacity="${look.alpha}" stroke-width="${look.width}"`
      + ' stroke-linejoin="round"/>';
  }
  // The ground line, and one figure per ring at the right.
  let figs = '';
  const figAlpha = historical ? look.alpha : T.modern.fig.step_alpha;
  // The historical figures take the Fell size of their step; the modern
  // ones take the chrome cap step from the stylesheet.
  const size = historical ? ` font-size="${CHART.text.steps[T.text.step].fellPx}"` : '';
  for (let k = 1; k <= rings; k++) {
    const word = historical ? toRoman(k).toLowerCase() : SC.profile_step({ k });
    figs += `<text x="${w}" y="${f1(yOf(k) + u / 2)}" text-anchor="end" dominant-baseline="middle"`
      + `${size} fill-opacity="${figAlpha}">${word}</text>`;
  }
  const mark = p.at >= 0
    ? `<path class="ck-at" d="M${f1((p.at + 0.5) * cw)} 2V${f1(base)}" stroke-opacity="${T.fig.at_alpha}"`
      + ` stroke-dasharray="2 2"/>`
    : '';
  return `<svg class="ck-terrain${historical ? ' hist' : ''}" viewBox="0 0 ${w} ${h}" width="100%"`
    + ` role="img" aria-hidden="true"><path d="M0 ${f1(base)}H${f1(n * cw)}" stroke="currentColor"`
    + ` stroke-opacity="${look.alpha / 2}"/>${shape}${figs}${mark}</svg>`;
}

/**
 * The peek of a ridge (round 084 card 01 with the terrain body of round
 * 086 card 06 b): the name, the cell count, the elevation and the one fact
 * that matters, that it is rock a torpedo dies on and a hull cannot cross.
 * Rock belongs to nobody, so the title takes the text ink.
 */
export function ridgePeek(r: RidgeAt, pointerCell: number, historical: boolean, mark = ''): CardSpec {
  // A seamount (R2633) says who named it, and an own find with no name
  // says that NAME, the action of the pinned peek, names it.
  const lines = [esc(SC.profile_foot)];
  if (r.seamount) lines.unshift(esc(SK.seamount_named_by({ commander: r.seamount.commander })));
  else if (r.find) lines.unshift(esc(SK.seamount_unnamed_find));
  return {
    size: 'peek', mark: markSvg('ridge', 'var(--text)'),
    title: r.name || SM.ridge_title, ink: 'neutral',
    // The ⚑ of a seamount name, which the overlay writes (R2633).
    titleHtml: mark || undefined,
    kind: SC.profile_kind({ cells: r.body.cells.length }),
    peekBody: `<div class="ck-sect">${profileSvg(ridgeProfile(r.body, pointerCell), historical)}</div>`,
    note: { lines },
  };
}

/**
 * What the water peek reads of a cell's depth and current (R2706): the
 * fields of the hello, each null while its flag is off, and the figures
 * that turn a current into the unit of the legend.
 */
export interface WaterDeep {
  /** The band field, or null while the flag `depth` is off. */
  depth: Uint8Array | null;
  /** The current field, or null while the flag `currents` is off. */
  currents: Int8Array | null;
  /** `CatalogRules.current_unit_px_h`. */
  unitPxH: number;
  /** The factor of the tide now; a negative factor turns the flow round. */
  tideFactor: number;
  /** Map seconds a wall second (`GameState.tacticalScale`). */
  tacticalScale: number;
}

/** The depth and the current of a world, each field under its own flag. */
export function waterDeepOf(state: GameState): WaterDeep {
  const currents = state.hasFeatureFlag('currents') ? state.currents : null;
  return {
    depth: state.hasFeatureFlag('depth') ? state.depthBands : null,
    currents,
    unitPxH: state.rules.currentUnitPxH,
    tideFactor: currents ? state.tideFactor() : 1,
    tacticalScale: state.tacticalScale,
  };
}

/** The scratch pair `currentAt` writes into. */
const flow: [number, number] = [0, 0];

/**
 * The depth line and the current line of the water peek of a surveyed
 * cell (R2706, ui-map-interface.md §8 "The peek body"). The depth is the
 * band word and the sounding of the chart (card 02) in metres. The
 * current is the flow at the tide now in px a wall minute, the unit of
 * the CURRENT section of the legend, and the true bearing it flows
 * toward. A cell with no band writes no depth line; a world with no
 * current field writes no current line.
 */
export function waterDeepLines(w: WaterDeep, cell: number): string[] {
  const out: string[] = [];
  const word = w.depth ? bandWord(bandAt(w.depth, cell), true) : '';
  if (w.depth && word !== '') {
    out.push(SM.water_depth({ band: word, m: groupThousands(soundingMetres(w.depth)[cell] ?? 0) }));
  }
  if (w.currents) {
    currentAt(w.currents, cell, w.unitPxH, flow);
    const vx = flow[0] * w.tideFactor, vy = flow[1] * w.tideFactor;
    // The legend's figure: px a map hour to px a wall minute, one place.
    const n = Math.round((Math.hypot(vx, vy) * w.tacticalScale) / 6) / 10;
    if (n === 0) {
      out.push(SM.water_current_none);
    } else {
      // Screen y grows south, so north is −y.
      const brg = Math.round(((Math.atan2(vx, -vy) * 180) / Math.PI + 360) % 360) % 360;
      out.push(SM.water_current({ n, brg: String(brg).padStart(3, '0') }));
    }
  }
  return out;
}
